/* eslint-disable react/jsx-props-no-spreading */
import { Controller } from 'react-hook-form';

// Utils
import cn from '../../../utils/cn';

// Styles
import styles from './BatchActionRender.module.css';

// Components
import TextField from '../Fields/TextField/TextField';
import DateTimeField from '../Fields/DateTimeField/DateTimeField';
import ColorField from '../Fields/ColorField/ColorField';
import TextareaField from '../Fields/TextareaField/TextareaField';
import TimeField from '../Fields/TimeField/TimeField';
import EnumField from '../Fields/EnumField/EnumField';
import SelectClassField from '../Fields/SelectClassField/SelectClassField';

/**
 * @typedef {object} BatchActionGroup
 * @property {string} label - Group label
 * @property {Array<{id: string, lib: string, type?: string, config?: object}>} options - Group options
 */

/**
 * BatchActionRender - Renders appropriate input based on selected action type
 * @param {object} props
 * @param {string} [props.label] - Input label
 * @param {import('react-hook-form').Control<any>} props.controlRhf - React Hook Form control
 * @param {Partial<ApiManagerTableFiltersSetAttributeMultipleString> |
 * Partial<ApiManagerTableFiltersSetAttributeMultipleSelectEnum> |
 * Partial<ApiManagerTableFiltersSetAttributeMultipleSelectClass> |
 * Partial<ApiManagerTableFiltersSetAttributeMultipleSelectOrCreateForm>} props.field - Field of the input
 * @param {string | null} props.type - Selected action ID
 * @param {string} [props.name] - Input name attribute
 * @param {string} [props.className] - Additional CSS classes
 * @param {boolean} [props.required] - Whether input is required
 * @param {string} [props.error] - Validation error object
 * @returns {JSX.Element | null} BatchActionRender component
 */
function BatchActionRender({
  type,
  name,
  label,
  controlRhf,
  field,
  className = '',
  required = false,
  error = null,
}) {
  if (!type || type === 'singleAction') return null;

  /**
   * Render input based on type
   * @returns {JSX.Element | null} BatchActionRender component
   */
  const renderInput = () => {
    if (!field.type) return null;

    switch (field.type) {
      case 'text':
        return (
          <Controller
            control={controlRhf}
            name={name}
            render={({ field: { ref, ...fieldInput } }) => (
              <TextField
                {...fieldInput}
                error={error}
              />
            )}
          />
        );

      case 'date':
        return (
          <Controller
            control={controlRhf}
            name={name}
            render={({ field: { ref, ...fieldInput } }) => (
              <DateTimeField
                theme="light"
                type="date"
                {...fieldInput}
                error={error}
              />
            )}
          />
        );

      case 'time':
        return (
          <Controller
            control={controlRhf}
            name={name}
            render={({ field: { ref, ...fieldInput } }) => (
              <TimeField
                placeholder="--:--"
                theme="light"
                {...fieldInput}
                error={error}
              />
            )}
          />
        );

      case 'color':
        return (
          <Controller
            control={controlRhf}
            name={name}
            render={({ field: { ref, ...fieldInput } }) => (
              <ColorField
                placeholder="#FFFFFF"
                theme="light"
                error={error}
                {...fieldInput}
              />
            )}
          />
        );

      case 'textarea':
        return (
          <Controller
            control={controlRhf}
            name={name}
            render={({ field: { ref, ...fieldInput } }) => (
              <TextareaField
                {...fieldInput}
                error={error}
              />
            )}
          />
        );

      case 'selectEnum':
        return (
          <Controller
            control={controlRhf}
            name={name}
            render={({ field: { ref, ...fieldInput } }) => (
              <EnumField
                theme="light"
                options={field.selectOptions}
                {...fieldInput}
                error={error}
              />
            )}
          />
        );

      case 'selectClass':
        return (
          <Controller
            control={controlRhf}
            name={name}
            render={({ field: { ref, ...fieldInput } }) => (
              <SelectClassField
                theme="light"
                selectOptionsUrl={field.selectOptionsUrl}
                {...fieldInput}
                error={error}
              />
            )}
          />
        );

      case 'selectOrCreateForm':
        // eslint-disable-next-line no-console
        console.warn('TODO FORM: Select or Create Form when api is ready');
        return null;

      default:
        return null;
    }
  };

  return (
    <div className={cn([styles.wrapper, className])}>
      {label && (
        <label htmlFor={name} className={styles.label}>
          {label}
          {required && <span className={styles.required}>*</span>}
        </label>
      )}

      <div className={styles['input-wrapper']}>
        {renderInput()}
      </div>
    </div>
  );
}

export default BatchActionRender;
